import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Button,
    Checkbox,
    Form,
    Input,
    Modal,
    Popconfirm,
    Select,
    Space,
    Switch,
    Table,
    Tabs,
    Tag,
    message,
} from 'antd';
import {
    DeleteOutlined,
    EditOutlined,
    PlusOutlined,
    ReloadOutlined,
    TeamOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import CommonLayout from '../../common/LayoutBlock';
import { useBreadcrumbs } from '../../context/BreadcrumbContext';
import { useAuthOrg } from '../../context/AuthOrgContext';
import { useMetaSetup } from '../../context/MetaSetupContext';
import leadDistributionService, {
    SalesCaller,
    SalesCallerInput,
    SalesUrl,
    SalesUrlInput,
    Weekday,
} from '../../apiServices/leadDistributionService';

const WEEKDAYS: Array<{ label: string; value: Weekday }> = [
    { label: 'Mon', value: 1 },
    { label: 'Tue', value: 2 },
    { label: 'Wed', value: 3 },
    { label: 'Thu', value: 4 },
    { label: 'Fri', value: 5 },
    { label: 'Sat', value: 6 },
    { label: 'Sun', value: 0 },
];
const DEFAULT_DAYS = WEEKDAYS.slice(0, 5).map(({ value }) => value);
const DEFAULT_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata';

type CallerFormValues = Omit<SalesCallerInput, 'orgId' | 'clientId'>;
type UrlFormValues = Omit<SalesUrlInput, 'orgId' | 'clientId'>;
type LeadDistributionFlow = 'CALLER' | 'URL';

const LeadDistribution: React.FC = () => {
    const navigate = useNavigate();
    const { setBreadcrumbs } = useBreadcrumbs();
    const { availableOrgs, currentOrg } = useAuthOrg();
    const { fetchWabaDetails, orgWabaData } = useMetaSetup();
    const [callerForm] = Form.useForm<CallerFormValues>();
    const [urlForm] = Form.useForm<UrlFormValues>();

    const [selectedOrgId, setSelectedOrgId] = useState(currentOrg?.orgId || '');
    const [selectedClientId, setSelectedClientId] = useState('');
    const [activeFlow, setActiveFlow] = useState<LeadDistributionFlow>('CALLER');
    const [callers, setCallers] = useState<SalesCaller[]>([]);
    const [urls, setUrls] = useState<SalesUrl[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingCaller, setEditingCaller] = useState<SalesCaller | null>(null);
    const [editingUrl, setEditingUrl] = useState<SalesUrl | null>(null);
    const [changingIds, setChangingIds] = useState<Set<string>>(new Set());

    useEffect(() => {
        setBreadcrumbs([
            { title: <div className="text-lg cursor-pointer" onClick={() => navigate('/settings')}>Settings</div> },
            { title: <div className="text-lg">Lead Distribution</div> },
        ]);
    }, [navigate, setBreadcrumbs]);

    useEffect(() => {
        if (!selectedOrgId && currentOrg?.orgId) setSelectedOrgId(currentOrg.orgId);
    }, [currentOrg?.orgId, selectedOrgId]);

    useEffect(() => { fetchWabaDetails(); }, [fetchWabaDetails]);

    const clientOptions = useMemo(() => {
        const org = orgWabaData.find((item) => item.orgId === selectedOrgId);
        return (org?.wabaDetails || []).map((waba) => ({
            value: waba.clientId,
            label: waba.subdomain || waba.wabaNumber || waba.clientId,
        }));
    }, [orgWabaData, selectedOrgId]);

    useEffect(() => {
        setSelectedClientId((current) =>
            clientOptions.some((option) => option.value === current) ? current : clientOptions[0]?.value || ''
        );
    }, [clientOptions]);

    const loadData = useCallback(async () => {
        if (!selectedOrgId || !selectedClientId) {
            setCallers([]);
            setUrls([]);
            return;
        }
        setLoading(true);
        try {
            const [callerRecords, urlRecords] = await Promise.all([
                leadDistributionService.listCallers(selectedOrgId, selectedClientId),
                leadDistributionService.listUrls(selectedOrgId, selectedClientId),
            ]);
            setCallers(Array.isArray(callerRecords) ? callerRecords : []);
            setUrls(Array.isArray(urlRecords) ? urlRecords : []);
        } catch (error: any) {
            message.error(error?.message || 'Failed to load lead distribution data');
            setCallers([]);
            setUrls([]);
        } finally {
            setLoading(false);
        }
    }, [selectedClientId, selectedOrgId]);

    useEffect(() => { loadData(); }, [loadData]);

    const markChanging = (id: string, changing: boolean) => {
        setChangingIds((current) => {
            const next = new Set(current);
            if (changing) next.add(id); else next.delete(id);
            return next;
        });
    };

    const openCallerModal = (caller?: SalesCaller) => {
        setEditingCaller(caller || null);
        setEditingUrl(null);
        callerForm.setFieldsValue(caller ? {
            name: caller.name,
            phoneNumber: caller.phoneNumber,
            isActive: caller.isActive,
            availabilityEnabled: caller.availabilityEnabled,
            startTime: caller.startTime,
            endTime: caller.endTime,
            timezone: caller.timezone,
            workingDays: caller.workingDays,
        } : {
            name: '', phoneNumber: '', isActive: true, availabilityEnabled: true,
            startTime: '09:00', endTime: '18:00', timezone: DEFAULT_TIMEZONE, workingDays: DEFAULT_DAYS,
        });
        setModalOpen(true);
    };

    const openUrlModal = (url?: SalesUrl) => {
        setEditingUrl(url || null);
        setEditingCaller(null);
        urlForm.setFieldsValue(url ? { name: url.name, url: url.url, isActive: url.isActive } : { name: '', url: '', isActive: true });
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setEditingCaller(null);
        setEditingUrl(null);
        callerForm.resetFields();
        urlForm.resetFields();
    };

    const saveCaller = async () => {
        try {
            const values = await callerForm.validateFields();
            setSaving(true);
            const payload: SalesCallerInput = { orgId: selectedOrgId, clientId: selectedClientId, ...values };
            if (editingCaller) {
                await leadDistributionService.updateCaller(editingCaller.id, payload);
                message.success('Sales caller updated');
            } else {
                await leadDistributionService.createCaller(payload);
                message.success('Sales caller added');
            }
            closeModal();
            await loadData();
        } catch (error: any) {
            if (!error?.errorFields) message.error(error?.message || 'Failed to save sales caller');
        } finally { setSaving(false); }
    };

    const saveUrl = async () => {
        try {
            const values = await urlForm.validateFields();
            setSaving(true);
            const payload: SalesUrlInput = { orgId: selectedOrgId, clientId: selectedClientId, ...values };
            if (editingUrl) {
                await leadDistributionService.updateUrl(editingUrl.id, payload);
                message.success('URL updated');
            } else {
                await leadDistributionService.createUrl(payload);
                message.success('URL added');
            }
            closeModal();
            await loadData();
        } catch (error: any) {
            if (!error?.errorFields) message.error(error?.message || 'Failed to save URL');
        } finally { setSaving(false); }
    };

    const toggleCaller = async (caller: SalesCaller, isActive: boolean) => {
        markChanging(caller.id, true);
        try {
            await leadDistributionService.setCallerEnabled(caller.id, isActive);
            setCallers((current) => current.map((item) => item.id === caller.id ? { ...item, isActive } : item));
        } catch (error: any) { message.error(error?.message || 'Failed to update caller status'); }
        finally { markChanging(caller.id, false); }
    };

    const toggleUrl = async (url: SalesUrl, isActive: boolean) => {
        markChanging(url.id, true);
        try {
            await leadDistributionService.setUrlStatus(url.id, isActive);
            setUrls((current) => current.map((item) => item.id === url.id ? { ...item, isActive } : item));
        } catch (error: any) { message.error(error?.message || 'Failed to update URL status'); }
        finally { markChanging(url.id, false); }
    };

    const removeCaller = async (caller: SalesCaller) => {
        markChanging(caller.id, true);
        try { await leadDistributionService.deleteCaller(caller.id); setCallers((current) => current.filter((item) => item.id !== caller.id)); }
        catch (error: any) { message.error(error?.message || 'Failed to remove caller'); }
        finally { markChanging(caller.id, false); }
    };

    const removeUrl = async (url: SalesUrl) => {
        markChanging(url.id, true);
        try { await leadDistributionService.deleteUrl(url.id); setUrls((current) => current.filter((item) => item.id !== url.id)); }
        catch (error: any) { message.error(error?.message || 'Failed to remove URL'); }
        finally { markChanging(url.id, false); }
    };

    const callerColumns = [
        { title: 'Caller', key: 'caller', render: (_: unknown, caller: SalesCaller) => <div><div className="font-medium text-gray-900">{caller.name}</div><div className="text-xs text-gray-500">{caller.phoneNumber}</div></div> },
        { title: 'Availability', key: 'availability', render: (_: unknown, caller: SalesCaller) => <div><div>{caller.startTime}–{caller.endTime}</div><div className="text-xs text-gray-400">{caller.timezone}</div></div> },
        { title: 'Days', dataIndex: 'workingDays', key: 'workingDays', render: (days: Weekday[] = []) => <div className="flex flex-wrap gap-1">{WEEKDAYS.filter(({ value }) => days.includes(value)).map((day) => <Tag key={day.value} className="!mr-0">{day.label}</Tag>)}</div> },
        { title: 'Status', key: 'status', render: (_: unknown, caller: SalesCaller) => <Switch checked={caller.isActive} loading={changingIds.has(caller.id)} onChange={(checked) => toggleCaller(caller, checked)} checkedChildren="On" unCheckedChildren="Off" /> },
        { title: 'Actions', key: 'actions', render: (_: unknown, caller: SalesCaller) => <Space><Button type="text" icon={<EditOutlined />} onClick={() => openCallerModal(caller)} /><Popconfirm title="Remove caller?" onConfirm={() => removeCaller(caller)}><Button type="text" danger loading={changingIds.has(caller.id)} icon={<DeleteOutlined />} /></Popconfirm></Space> },
    ];

    const urlColumns = [
        { title: 'Name', dataIndex: 'name', key: 'name' },
        { title: 'URL', dataIndex: 'url', key: 'url', render: (url: string) => <span className="break-all">{url}</span> },
        { title: 'Status', key: 'status', render: (_: unknown, url: SalesUrl) => <Switch checked={url.isActive} loading={changingIds.has(url.id)} onChange={(checked) => toggleUrl(url, checked)} checkedChildren="On" unCheckedChildren="Off" /> },
        { title: 'Actions', key: 'actions', render: (_: unknown, url: SalesUrl) => <Space><Button type="text" icon={<EditOutlined />} onClick={() => openUrlModal(url)} /><Popconfirm title="Remove URL?" onConfirm={() => removeUrl(url)}><Button type="text" danger loading={changingIds.has(url.id)} icon={<DeleteOutlined />} /></Popconfirm></Space> },
    ];

    const renderCallerForm = () => <Form form={callerForm} layout="vertical" className="pt-3"><Form.Item name="name" label="Caller name" rules={[{ required: true }, { max: 100 }]}><Input placeholder="Sales representative" /></Form.Item><Form.Item name="phoneNumber" label="Phone number" rules={[{ required: true }, { pattern: /^\+[1-9]\d{7,14}$/, message: 'Enter a valid E.164 number' }]}><Input placeholder="+919876543210" /></Form.Item><div className="grid grid-cols-2 gap-4"><Form.Item name="startTime" label="Available from" rules={[{ required: true }]}><Input type="time" /></Form.Item><Form.Item name="endTime" label="Available until" rules={[{ required: true }]}><Input type="time" /></Form.Item></div><Form.Item name="timezone" label="Timezone" rules={[{ required: true }]}><Input placeholder="Asia/Kolkata" /></Form.Item><Form.Item name="workingDays" label="Working days" rules={[{ required: true, type: 'array', min: 1 }]}><Checkbox.Group options={WEEKDAYS} /></Form.Item><Form.Item name="isActive" label="Status" valuePropName="checked"><Switch checkedChildren="On" unCheckedChildren="Off" /></Form.Item><Form.Item name="availabilityEnabled" label="Use availability schedule" valuePropName="checked"><Switch checkedChildren="On" unCheckedChildren="Off" /></Form.Item></Form>;
    const renderUrlForm = () => <Form form={urlForm} layout="vertical" className="pt-3"><Form.Item name="name" label="Name" rules={[{ required: true, message: 'Enter a name' }, { max: 100 }]}><Input placeholder="Campaign landing page" /></Form.Item><Form.Item name="url" label="URL" rules={[{ required: true, type: 'url', message: 'Enter a valid URL' }]}><Input placeholder="https://example.com/landing-page" /></Form.Item><Form.Item name="isActive" label="Status" valuePropName="checked"><Switch checkedChildren="On" unCheckedChildren="Off" /></Form.Item></Form>;

    const flowItems = [
        { key: 'CALLER', label: <span><TeamOutlined /> Sales callers</span>, children: <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"><div className="flex items-center justify-between border-b border-gray-100 px-6 py-4"><div><h2 className="font-semibold text-gray-900">Sales caller pool</h2><p className="text-xs text-gray-500">Available callers are selected by the caller distribution API.</p></div><Space><Button icon={<ReloadOutlined />} loading={loading} onClick={loadData}>Refresh</Button><Button type="primary" icon={<PlusOutlined />} onClick={() => openCallerModal()}>Add caller</Button></Space></div><Table rowKey="id" columns={callerColumns} dataSource={callers} loading={loading} pagination={{ pageSize: 10 }} scroll={{ x: 850 }} locale={{ emptyText: 'No sales callers configured.' }} /></div> },
        { key: 'URL', label: 'URLs', children: <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"><div className="flex items-center justify-between border-b border-gray-100 px-6 py-4"><div><h2 className="font-semibold text-gray-900">URL pool</h2><p className="text-xs text-gray-500">URLs are selected by the URL distribution API.</p></div><Space><Button icon={<ReloadOutlined />} loading={loading} onClick={loadData}>Refresh</Button><Button type="primary" icon={<PlusOutlined />} onClick={() => openUrlModal()}>Add URL</Button></Space></div><Table rowKey="id" columns={urlColumns} dataSource={urls} loading={loading} pagination={{ pageSize: 10 }} scroll={{ x: 700 }} locale={{ emptyText: 'No URLs configured.' }} /></div> },
    ];

    return <CommonLayout>
        <div className="min-h-screen bg-gray-50 p-6 md:p-8">
            <div className="mb-7 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                <div>
                    <h1 className="mb-1 text-2xl font-bold text-gray-900">Lead Distribution</h1>
                    <p className="max-w-2xl text-sm text-gray-500">Configure separate sales caller and URL pools for each WhatsApp client.</p>
                </div>
                <div className="flex w-full flex-col gap-3 lg:w-auto lg:flex-row">
                    <Select className="lg:w-64" showSearch optionFilterProp="label" value={selectedOrgId || undefined} options={availableOrgs.map((org) => ({ value: org.orgId, label: org.orgName }))} placeholder="Select an organization" onChange={setSelectedOrgId} />
                    <Select className="lg:w-64" showSearch optionFilterProp="label" value={selectedClientId || undefined} options={clientOptions} placeholder="Select a WhatsApp client" disabled={!selectedOrgId || !clientOptions.length} onChange={setSelectedClientId} />
                </div>
            </div>
            <Tabs
                activeKey={activeFlow}
                items={flowItems}
                onChange={(key) => setActiveFlow(key as LeadDistributionFlow)}
                type='card'
            />
        </div>
        <Modal
            title={activeFlow === 'CALLER' ? (editingCaller ? 'Edit sales caller' : 'Add sales caller') : (editingUrl ? 'Edit URL' : 'Add URL')}
            open={modalOpen} okText={editingCaller || editingUrl ? 'Save changes' : 'Add'}
            confirmLoading={saving}
            onOk={activeFlow === 'CALLER' ? saveCaller : saveUrl}
            onCancel={closeModal}
            destroyOnClose>{activeFlow === 'CALLER' ? renderCallerForm() : renderUrlForm()}
        </Modal>
    </CommonLayout>;
};

export default LeadDistribution;
